import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Loader2, FileX, Plus, Printer, Download, Check, Pencil } from 'lucide-react';
import { CaseTopBar } from '../components/editor/CaseTopBar';
import { DocumentsPanel } from '../components/editor/DocumentsPanel';
import { ResultsPanel } from '../components/editor/ResultsPanel';
import { PDFEditor } from '../components/pdf';
import { DocxEditorView } from '../components/docx';
import { EmailEditorView } from '../components/email';
import { PSTEditorView } from '../components/pst';
import { Button } from '../components/ui/Button';
import { getFileIconByName } from '../components/FileIcons';
import {
  casesApi,
  documentsApi,
  redactionApi,
  pstApi,
  apiHelpers,
  type HtmlContent,
} from '../lib/api';
import { useReasons } from '../hooks/useReasons';
import type { Case, CaseDocument, DocumentStatus, EntitySuggestion } from '../types';

export function CaseEditorPage() {
  const navigate = useNavigate();
  const { caseId } = useParams<{ caseId: string }>();

  // Case state
  const [caseData, setCaseData] = useState<Case | null>(null);
  const [documents, setDocuments] = useState<CaseDocument[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Document viewer state
  const [selectedDocumentId, setSelectedDocumentId] = useState<string | null>(null);
  const [currentDocument, setCurrentDocument] = useState<CaseDocument | null>(null);
  const [pdfData, setPdfData] = useState<ArrayBuffer | null>(null);
  const [htmlContent, setHtmlContent] = useState<HtmlContent | null>(null);
  const [isLoadingDocument, setIsLoadingDocument] = useState(false);
  const [documentError, setDocumentError] = useState<string | null>(null);
  const [isFinalizingPst, setIsFinalizingPst] = useState(false);

  // Panel state
  const [isDocumentsPanelCollapsed, setIsDocumentsPanelCollapsed] = useState(false);
  const [isResultsPanelCollapsed, setIsResultsPanelCollapsed] = useState(false);

  // Entity suggestions state
  const [suggestions, setSuggestions] = useState<EntitySuggestion[]>([]);
  const [currentJobId, setCurrentJobId] = useState<string | null>(null);
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState<number | null>(null);
  const [isFinalizing, setIsFinalizing] = useState(false);

  // Reasons state
  const { reasons } = useReasons();
  const [entityReasons, setEntityReasons] = useState<Map<number, string>>(new Map());

  // Load case data
  useEffect(() => {
    if (!caseId) return;

    const loadCase = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const response = await casesApi.getCase(caseId);
        setCaseData(response);
        setDocuments(response.documents);

        // Auto-select first document if available
        if (response.documents.length > 0) {
          setSelectedDocumentId(response.documents[0].id);
        }
      } catch (err) {
        console.error('Failed to load case:', err);
        setError(apiHelpers.handleError(err));
      } finally {
        setIsLoading(false);
      }
    };

    loadCase();
  }, [caseId]);

  // Get child documents for the current document (used for PST containers)
  const childDocuments = useMemo(() => {
    if (!currentDocument) return [];
    return documents.filter((doc) => doc.parent_id === currentDocument.id);
  }, [documents, currentDocument]);

  // Check if current document is a PST container
  const isPstContainer = useMemo(() => {
    if (!currentDocument) return false;
    const fileType = currentDocument.name.split('.').pop()?.toLowerCase();
    return fileType === 'pst' && (currentDocument.is_container || childDocuments.length > 0);
  }, [currentDocument, childDocuments]);

  // Load selected document
  useEffect(() => {
    if (!selectedDocumentId) {
      setCurrentDocument(null);
      setPdfData(null);
      setHtmlContent(null);
      setSuggestions([]);
      setCurrentJobId(null);
      setEntityReasons(new Map());
      return;
    }

    const doc = documents.find((d) => d.id === selectedDocumentId);
    if (!doc) return;

    setCurrentDocument(doc);
    loadDocumentContent(doc);
    loadSuggestionsForDocument(selectedDocumentId);
  }, [selectedDocumentId, documents]);

  const loadDocumentContent = async (doc: CaseDocument) => {
    setIsLoadingDocument(true);
    setDocumentError(null);
    setPdfData(null);
    setHtmlContent(null);

    // Get file extension from filename (more reliable than MIME type)
    const fileType = doc.name.split('.').pop()?.toLowerCase();

    try {
      if (fileType === 'pdf') {
        const { blob } = await documentsApi.downloadDocument(doc.id, 'original');
        const arrayBuffer = await blob.arrayBuffer();
        setPdfData(arrayBuffer);
      } else if (['docx', 'doc', 'eml', 'msg'].includes(fileType || '')) {
        const content = await documentsApi.getHtmlContent(doc.id);
        if (!content.success) {
          throw new Error(content.error_message || 'Failed to load document content');
        }
        setHtmlContent(content);
      } else if (fileType === 'pst') {
        // PST files are containers - their children are shown in DocumentsPanel
        // No content to load for the container itself
      } else {
        // For other file types, just show a placeholder
        setDocumentError('Preview not available for this file type');
      }
    } catch (err) {
      console.error('Failed to load document:', err);
      setDocumentError(apiHelpers.handleError(err));
    } finally {
      setIsLoadingDocument(false);
    }
  };

  const loadSuggestionsForDocument = async (documentId: string) => {
    setIsLoadingSuggestions(true);
    setSuggestions([]);
    setSelectedSuggestionIndex(null);

    try {
      // Find any existing job for this document
      const jobs = await redactionApi.getJobs({ document_id: documentId, status: 'awaiting_approval' });

      if (jobs.length > 0) {
        const job = jobs[0];
        setCurrentJobId(job.job_id);

        // Load suggestions
        // For PST files, the 'page' field contains the messageIndex
        const suggestionsResponse = await redactionApi.getSuggestions(job.job_id);
        const mappedSuggestions = suggestionsResponse.suggestions.map((s) => ({
          index: s.index,
          entityType: s.entity_type,
          text: s.text,
          confidence: s.confidence,
          page: s.page,
          bounds: s.bounds,
          replacementText: s.replacement_text,
          status: s.status,
          // Include messageIndex from page field for PST compatibility
          messageIndex: s.page,
        }));
        setSuggestions(mappedSuggestions);
      }
    } catch (err) {
      console.error('Failed to load suggestions:', err);
    } finally {
      setIsLoadingSuggestions(false);
    }
  };

  const handleBack = () => {
    navigate('/');
  };

  const handleUploadDocuments = () => {
    // TODO: Open upload modal
    console.log('Upload documents');
  };

  const handleDocumentStatusChange = async (status: DocumentStatus) => {
    if (!selectedDocumentId || !caseId) return;

    try {
      await casesApi.updateDocumentStatus(caseId, selectedDocumentId, status);

      // Update local state
      setDocuments((prev) =>
        prev.map((doc) =>
          doc.id === selectedDocumentId ? { ...doc, status } : doc
        )
      );
      setCurrentDocument((prev) => (prev ? { ...prev, status } : prev));
    } catch (err) {
      console.error('Failed to update document status:', err);
    }
  };

  const handleDownload = async (version: 'original' | 'redacted') => {
    if (!selectedDocumentId || !currentDocument) return;

    try {
      // Provide fallback filename based on version and document name
      const fallbackFilename = version === 'redacted'
        ? `redacted_${currentDocument.name}`
        : currentDocument.name;
      const { blob, filename } = await documentsApi.downloadDocument(
        selectedDocumentId,
        version,
        fallbackFilename
      );
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      // Use filename from Content-Disposition header (handles format conversions like PST -> ZIP)
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to download document:', err);
    }
  };

  const handleApprove = async (indices: number[]) => {
    if (!currentJobId) return;

    try {
      // Build entity_reasons map for approved entities
      const entityReasonsObj: Record<number, string> = {};
      indices.forEach((index) => {
        const reason = entityReasons.get(index);
        if (reason) {
          entityReasonsObj[index] = reason;
        }
      });

      await redactionApi.approveSuggestions(currentJobId, {
        approved_entities: indices,
        entity_reasons: entityReasonsObj,
        apply_redactions: false, // Don't apply redactions immediately - wait for Finalize
      });

      // Update local state - include replacementText with reason if provided
      setSuggestions((prev) =>
        prev.map((s) => {
          if (!indices.includes(s.index)) return s;
          const reason = entityReasons.get(s.index);
          return {
            ...s,
            status: 'approved' as const,
            // Update replacementText with the reason (wrapped in brackets) if provided
            replacementText: reason ? `[${reason}]` : s.replacementText,
          };
        })
      );
    } catch (err) {
      console.error('Failed to approve suggestions:', err);
    }
  };

  const handleReject = async (indices: number[]) => {
    if (!currentJobId) return;

    try {
      await redactionApi.approveSuggestions(currentJobId, {
        approved_entities: [],
        rejected_entities: indices,
        entity_reasons: {},
        apply_redactions: false, // Don't apply redactions immediately - wait for Finalize
      });

      // Update local state
      setSuggestions((prev) =>
        prev.map((s) =>
          indices.includes(s.index) ? { ...s, status: 'rejected' as const } : s
        )
      );
    } catch (err) {
      console.error('Failed to reject suggestions:', err);
    }
  };

  const handleAddManualRedaction = () => {
    // TODO: Open manual redaction dialog
    console.log('Add manual redaction');
  };

  const handleNavigateToSuggestion = (suggestion: EntitySuggestion) => {
    setSelectedSuggestionIndex(suggestion.index);
  };

  // PST finalize handler - creates ZIP of redacted children
  const handlePstFinalize = useCallback(async () => {
    if (!selectedDocumentId || !caseId) return;

    setIsFinalizingPst(true);
    try {
      // Call the finalize endpoint which creates a ZIP of redacted children
      const result = await pstApi.finalize(selectedDocumentId);

      if (result.status === 'completed') {
        // Refresh case to get updated document status
        const response = await casesApi.getCase(caseId);
        setCaseData(response);
        setDocuments(response.documents);

        // Download the ZIP file
        if (result.zip_document_id) {
          const { blob, filename } = await documentsApi.downloadDocument(
            result.zip_document_id,
            'original',
            `${currentDocument?.name || 'pst'}_redacted.zip`
          );
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = filename;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
        }
      }
    } catch (err) {
      console.error('Failed to finalize PST:', err);
    } finally {
      setIsFinalizingPst(false);
    }
  }, [selectedDocumentId, caseId, currentDocument]);

  const handleReasonChange = useCallback((index: number, reason: string) => {
    setEntityReasons((prev) => {
      const newMap = new Map(prev);
      if (reason) {
        newMap.set(index, reason);
      } else {
        newMap.delete(index);
      }
      return newMap;
    });
  }, []);

  // Finalize document: apply all approved redactions and mark as completed
  const handleFinalizeDocument = useCallback(async () => {
    if (!currentJobId || !selectedDocumentId || !caseId) return;

    // Check if there are any approved suggestions
    const approvedCount = suggestions.filter((s) => s.status === 'approved').length;
    if (approvedCount === 0) {
      console.warn('No approved suggestions to finalize');
      return;
    }

    setIsFinalizing(true);
    try {
      // Step 1: Apply all approved redactions to create the redacted file
      const result = await redactionApi.applyJobRedactions(currentJobId);

      if (result.status === 'completed' || result.status === 'no_changes') {
        // Step 2: Mark document as finalized (completed)
        await casesApi.finalizeDocument(caseId, selectedDocumentId);

        // Update local document state
        setDocuments((prev) =>
          prev.map((doc) =>
            doc.id === selectedDocumentId ? { ...doc, status: 'completed' as DocumentStatus } : doc
          )
        );
        setCurrentDocument((prev) =>
          prev ? { ...prev, status: 'completed' as DocumentStatus } : prev
        );

        // Refresh case to get updated status
        const response = await casesApi.getCase(caseId);
        setCaseData(response);

        // Reload document to show redacted version
        if (currentDocument) {
          loadDocumentContent(currentDocument);
        }
      }
    } catch (err) {
      console.error('Failed to finalize document:', err);
    } finally {
      setIsFinalizing(false);
    }
  }, [currentJobId, selectedDocumentId, caseId, suggestions, currentDocument]);

  // Unfinalize document: allow re-editing by changing status back to 'redacted'
  const handleUnfinalize = useCallback(async () => {
    if (!selectedDocumentId || !caseId) return;

    try {
      await casesApi.updateDocumentStatus(caseId, selectedDocumentId, 'redacted');

      // Update local state
      setDocuments((prev) =>
        prev.map((doc) =>
          doc.id === selectedDocumentId ? { ...doc, status: 'redacted' as DocumentStatus } : doc
        )
      );
      setCurrentDocument((prev) =>
        prev ? { ...prev, status: 'redacted' as DocumentStatus } : prev
      );

      // Refresh case to get updated status
      const response = await casesApi.getCase(caseId);
      setCaseData(response);
    } catch (err) {
      console.error('Failed to unfinalize document:', err);
    }
  }, [selectedDocumentId, caseId]);

  // Handle print
  const handlePrint = useCallback(() => {
    window.print();
  }, []);

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen bg-background">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-8 h-8 animate-spin text-[#7035C4]" />
          <p className="text-muted-foreground">Loading case...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error || !caseData) {
    return (
      <div className="flex items-center justify-center h-screen bg-background">
        <div className="text-center">
          <p className="text-destructive mb-4">{error || 'Case not found'}</p>
          <Button variant="outline" onClick={handleBack}>
            Back to Cases
          </Button>
        </div>
      </div>
    );
  }

  const fileType = currentDocument?.name.split('.').pop()?.toLowerCase();

  return (
    <div className="h-screen flex flex-col bg-background">
      {/* Top Bar */}
      <CaseTopBar
        caseData={caseData}
        currentDocumentStatus={currentDocument?.status}
        onBack={handleBack}
        onUploadDocuments={handleUploadDocuments}
        onDocumentStatusChange={handleDocumentStatusChange}
        isDocumentSelected={!!selectedDocumentId}
        isDocumentFinalized={currentDocument?.status === 'completed'}
      />

      {/* Main Content Area */}
      <div className="flex-1 flex overflow-hidden">
        {/* Documents Panel (Left) */}
        <DocumentsPanel
          documents={documents}
          selectedDocumentId={selectedDocumentId}
          onSelectDocument={setSelectedDocumentId}
          isCollapsed={isDocumentsPanelCollapsed}
          onToggleCollapse={() => setIsDocumentsPanelCollapsed(!isDocumentsPanelCollapsed)}
        />

        {/* Document Viewer + Results Panel */}
        <div className="flex-1 flex flex-col overflow-hidden min-w-0">
          {/* Document Viewer (Center) */}
          <div className="flex-1 overflow-hidden bg-gray-50 min-h-0 flex flex-col">
            {isLoadingDocument ? (
              <div className="flex items-center justify-center h-full">
                <div className="flex flex-col items-center gap-4">
                  <Loader2 className="w-8 h-8 animate-spin text-[#7035C4]" />
                  <p className="text-muted-foreground">Loading document...</p>
                </div>
              </div>
            ) : documentError ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <FileX className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                  <p className="text-gray-500">{documentError}</p>
                </div>
              </div>
            ) : !selectedDocumentId ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <FileX className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                  <p className="text-gray-500 mb-4">No document selected</p>
                  {documents.length === 0 && (
                    <Button
                      onClick={handleUploadDocuments}
                      className="bg-[#7035C4] hover:bg-[#5c2a9e] text-white"
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Upload Documents
                    </Button>
                  )}
                </div>
              </div>
            ) : (fileType === 'pdf' && pdfData) ||
                 ((fileType === 'docx' || fileType === 'doc') && htmlContent) ||
                 ((fileType === 'eml' || fileType === 'msg') && htmlContent) ||
                 isPstContainer ? (
              <div className="flex flex-col flex-1 min-h-0 overflow-hidden">
                {/* Document Header */}
                <div className="bg-card border-b border-border px-6 py-4 shrink-0">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-primary/10 rounded">
                        {getFileIconByName(currentDocument?.name || '')}
                      </div>
                      <div>
                        <h3 className="font-medium">{currentDocument?.name || 'Document'}</h3>
                        <p className="text-sm text-muted-foreground">
                          {fileType === 'pdf' && 'PDF Document'}
                          {(fileType === 'eml' || fileType === 'msg') && 'Email'}
                          {(fileType === 'docx' || fileType === 'doc') && 'Word Document'}
                          {fileType === 'pst' && 'PST Archive'}
                        </p>
                      </div>
                    </div>

                    {/* Header Actions */}
                    <div className="flex items-center gap-2">
                      {/* Print button - only show when finalized */}
                      {currentDocument?.status === 'completed' && (
                        <button
                          onClick={handlePrint}
                          className="flex items-center gap-2 px-3 py-2 hover:bg-muted rounded-[var(--radius-button)] transition-colors"
                          title="Print (Ctrl+P)"
                        >
                          <Printer className="w-4 h-4" />
                          <span className="text-sm hidden sm:inline">Print</span>
                        </button>
                      )}

                      {/* Download button - only show when finalized */}
                      {currentDocument?.status === 'completed' && (
                        <div className="relative group">
                          <button
                            className="flex items-center gap-2 px-3 py-2 hover:bg-muted rounded-[var(--radius-button)] transition-colors"
                          >
                            <Download className="w-4 h-4" />
                            <span className="text-sm hidden sm:inline">Download</span>
                          </button>
                          <div className="absolute right-0 top-full mt-1 bg-card border border-border rounded-[var(--radius)] shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-10 min-w-[120px]">
                            <button
                              onClick={() => handleDownload('original')}
                              className="block w-full px-4 py-2 text-sm text-left hover:bg-muted transition-colors"
                            >
                              Original
                            </button>
                            <button
                              onClick={() => handleDownload('redacted')}
                              className="block w-full px-4 py-2 text-sm text-left hover:bg-muted transition-colors"
                            >
                              Redacted
                            </button>
                          </div>
                        </div>
                      )}

                      {/* Edit button - only show when finalized, allows re-editing */}
                      {currentDocument?.status === 'completed' && (
                        <button
                          onClick={handleUnfinalize}
                          className="flex items-center gap-2 px-3 py-2 hover:bg-muted rounded-[var(--radius-button)] transition-colors"
                          title="Edit redactions"
                        >
                          <Pencil className="w-4 h-4" />
                          <span className="text-sm hidden sm:inline">Edit</span>
                        </button>
                      )}

                      {/* Finalize button - show when not finalized and has approved suggestions */}
                      {currentDocument?.status !== 'completed' && currentJobId && (
                        <button
                          onClick={handleFinalizeDocument}
                          disabled={isFinalizing || suggestions.filter((s) => s.status === 'approved').length === 0}
                          className="flex items-center gap-2 px-4 py-2 bg-black text-white rounded-md hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          title={suggestions.filter((s) => s.status === 'approved').length === 0 ? 'Approve at least one suggestion to finalize' : 'Apply redactions and finalize'}
                        >
                          {isFinalizing ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Check className="w-4 h-4" />
                          )}
                          <span>Finalize</span>
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Editor Content */}
                <div className="flex-1 overflow-hidden min-h-0">
                  {fileType === 'pdf' && pdfData ? (
                    <PDFEditor
                      pdfData={pdfData}
                      suggestions={suggestions}
                      selectedSuggestionIndex={selectedSuggestionIndex}
                      onSelectSuggestion={setSelectedSuggestionIndex}
                    />
                  ) : (fileType === 'docx' || fileType === 'doc') && htmlContent ? (
                    <DocxEditorView
                      htmlContent={htmlContent}
                      suggestions={suggestions}
                      selectedSuggestionIndex={selectedSuggestionIndex}
                      onSelectSuggestion={setSelectedSuggestionIndex}
                    />
                  ) : (fileType === 'eml' || fileType === 'msg') && htmlContent ? (
                    <EmailEditorView
                      htmlContent={htmlContent}
                      suggestions={suggestions}
                      selectedSuggestionIndex={selectedSuggestionIndex}
                      onSelectSuggestion={setSelectedSuggestionIndex}
                    />
                  ) : isPstContainer ? (
                    <PSTEditorView
                      documentId={selectedDocumentId!}
                      documentName={currentDocument?.name || 'PST Archive'}
                      childDocuments={childDocuments}
                      onFinalize={handlePstFinalize}
                      isFinalizingPst={isFinalizingPst}
                    />
                  ) : null}
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <FileX className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                  <p className="text-gray-500">Preview not available</p>
                </div>
              </div>
            )}
          </div>

          {/* Results Panel (Bottom) */}
          <ResultsPanel
            suggestions={suggestions}
            onApprove={handleApprove}
            onReject={handleReject}
            onAddManualRedaction={handleAddManualRedaction}
            isCollapsed={isResultsPanelCollapsed}
            onToggleCollapse={() => setIsResultsPanelCollapsed(!isResultsPanelCollapsed)}
            isLoading={isLoadingSuggestions}
            onNavigate={handleNavigateToSuggestion}
            selectedSuggestionIndex={selectedSuggestionIndex}
            reasons={reasons}
            entityReasons={entityReasons}
            onReasonChange={handleReasonChange}
          />
        </div>
      </div>
    </div>
  );
}
